import React, {
 useContext, useState, useEffect, useReducer, useMemo, useRef, 
} from 'react';
import {
 TextField, Grid, Autocomplete, Radio, FormControlLabel, 
} from '@mui/material';
import toast from 'react-hot-toast';
import PropTypes from 'prop-types';
import { Button } from '@/shared/components/Button';
import PaxDetailsTable from './PaxDetailsTable'; 
import { BookingContext } from '../../../../../../context/BookingContext';
import { GlobalContext } from '../../../../../../context/GlobalContext';
import { AuthContext } from '../../../../../../context/AuthContext';
import ShowTripInfo from './ShowTripInfo';

const initialState = {
  totalMeals: 0,
  mealTotalRate: 0,
  mealDate: null,
  selectedTrip: null,
  paxData: [],
  tripOption: [],
  selecttimingOption: null,
  reInitialValue: {},
  date: {
    startDate: new Date(),
    endDate: new Date(),
  },
};

function bookingReducer(state, action) {
  const { type, payload } = action;
  
  switch (type) {
    case 'SET_FIELD':
      return { ...state, [payload.field]: payload.value };
    case 'SET_RE_INITIAL_VALUE':
      return { ...state, reInitialValue: { ...state.reInitialValue, ...payload } };
    case 'RESET_FORM':
      return {
        ...initialState,
        tripOption: state.tripOption,
        reInitialValue: {
          mealName: state.reInitialValue.mealName,
          resName: state.reInitialValue.resName,
          price: state.reInitialValue.price,
          id: state.reInitialValue.id,
          mealOffereds: state.reInitialValue.mealOffereds,
          locations: state.reInitialValue.locations,
          format: state.reInitialValue.format,
          resLocation: state.reInitialValue.resLocation, // Preserve location selection
        },
      };
    default:
      throw new Error(`Unhandled action type: ${type}`);
  }
}

const TIMING_OPTIONS = [
  { value: '1', label: 'Breakfast' },
  { value: '2', label: 'Snacks' },
  { value: '3', label: 'Lunch' },
  { value: '4', label: 'Dinner' },
];

const TYPE_OPTIONS = [
  { value: '1', label: 'Fit' },
  { value: '2', label: 'Git' },
  { value: '3', label: 'Delivery' },
];

const BookingForm = ({
 mealName, resName, price, id, mealOffereds, locations, format, 
}) => {
  const { setShowAddTripModal, tripDataFlag, setMealBookingData } = useContext(BookingContext);
  const [state, dispatch] = useReducer(bookingReducer, initialState);
  const { apiGlobal } = useContext(GlobalContext);
  const [tripBookingData, setTripBookingData] = useState([]);
  const { agentId, userId } = useContext(AuthContext);
  const [selectedPaxs, setSelectedPaxs] = useState([]);
  
  const autocompleteRefs = {
    timing: useRef(),
    format: useRef(),
    location: useRef(),
  };

  // Helper functions
  const setField = (field, value) => dispatch({ type: 'SET_FIELD', payload: { field, value } });
  const setReInitialValue = payload => dispatch({ type: 'SET_RE_INITIAL_VALUE', payload });
  
  const formatDate = date => (date ? date.toISOString().split('T')[0] : '');
  
  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  };

  // Calculate day name based on meal date
  const calculateDayName = (mealdate) => {
    if (mealdate && state.date.startDate) {
      const mealDateObj = new Date(mealdate);
      const startDateObj = new Date(state.date.startDate);
      
      mealDateObj.setHours(0, 0, 0, 0);
      startDateObj.setHours(0, 0, 0, 0);
      
      const dayDifference = Math.floor((mealDateObj - startDateObj) / (1000 * 3600 * 24));
      const dayNumber = dayDifference >= 0 ? dayDifference + 1 : 1;

      setReInitialValue({ dayname: `Day ${dayNumber}` });
    }
  };

  // Reset form but preserve important values
  const resetForm = () => {
    const currentResLocation = state.reInitialValue.resLocation;
    dispatch({ type: 'RESET_FORM' });
    setSelectedPaxs([]);
    
    if (currentResLocation) {
      setReInitialValue({ resLocation: currentResLocation });
    }
    
    // Clear Autocomplete fields
    Object.values(autocompleteRefs).forEach((ref) => {
      // eslint-disable-next-line
      if (ref.current) ref.current.value = null;
    });
  };

  // Handle timing selection and fetch pax data
  const handleBookingCheck = async (option) => {
    if (!option || !state.mealDate || !state.selectedTrip?.value) {
      return;
    }
    
    setField('selecttimingOption', option);
    
    const formattedSelectedDate = formatDate(new Date(state.mealDate));
     const formData = new FormData();
     formData.append('trip_id', state.selectedTrip.value);
    formData.append('date', formattedSelectedDate);
    formData.append('meal_offered', option.value);
    try {
      const response = await fetch(`${apiGlobal}/agent/pax_data_check.php`, {
          method: 'POST',
          body: formData,
        });
      const data = await response.json();
      
      setField('paxData', data.status && data.message === 'Success' ? data.pax_details : []);
    } catch (e) {
      console.error(e);
      setField('paxData', []);
    }
  };

  const fetchTripBookingData = async (tripid) => {
    const formdata = new FormData();
    formdata.append('trip_id', tripid);
    try {
      const response = await fetch(`${apiGlobal}/trip_booking_data.php`, {
        method: 'POST',
        body: formdata,
      });
      const data = await response.json();
      console.log('Trip Booking Data:', data); // Log the response for debugging
      if (data?.status && data?.message === 'Success') {
        setTripBookingData(data.data); // ✅ correctly set data
      } else {
        setTripBookingData([]);
      }
    } catch (e) {
      console.error(e);
      setTripBookingData([]); // fallback in case of error
    }
  };
  


  // Reset timing when date changes
  const resetTiming = () => {
    // Clear the timing dropdown value
    if (autocompleteRefs.timing.current) {
      autocompleteRefs.timing.current.value = null;
    }
    
    // Reset timing-related state
    setField('selecttimingOption', null);
    setReInitialValue({ timing: null });
    
    // Clear pax data and selections
    setField('paxData', []);
    setSelectedPaxs([]);
  };

 // Handle meal date change
 const handleMealDateChange = (selectedDate) => {
  setField('mealDate', selectedDate);
  calculateDayName(selectedDate);
  
  // Reset timing when date changes
  resetTiming();
};

  // Handle form field changes
  const handleFieldChange = (fieldName, selectedOption) => {
    if (!selectedOption) return;
    
    setReInitialValue({ [fieldName]: selectedOption });
    
    if (fieldName === 'timing' && state.mealDate) {
      handleBookingCheck(selectedOption);
    }
  };

  // Auto-select location if there's only one option
  useEffect(() => {
    const locationOptions = state.reInitialValue?.locations || [];
    if (locationOptions.length === 1 && !state.reInitialValue.resLocation) {
      const singleLocation = {
        value: locationOptions[0].id,
        label: locationOptions[0].restaurant_name,
      };
      setReInitialValue({ resLocation: singleLocation });
    }
  }, [state.reInitialValue?.locations]);

  // Fetch trip options
  const fetchTripOption = async () => {
    const formData = new FormData();
    formData.append('agent_id', agentId);
    try {
      const response = await fetch(`${apiGlobal}/agent/trip_data.php`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      
      if (data.status && data.message === 'Success') {
        const tripOptions = data.data.map(item => ({
          value: item.id,
          label: item.name,
        }));
        setField('tripOption', tripOptions);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Fetch trip data when trip selection changes
  const fetchTripData = async (e, option) => {
    if (!option) {
      resetForm();
      return;
    }
    
    // Save current resLocation before resetting
    const currentResLocation = state.reInitialValue.resLocation;
    
    resetForm();
    setField('selectedTrip', option);
    fetchTripBookingData(option.value);
    // Restore resLocation after reset
    if (currentResLocation) {
      setReInitialValue({ resLocation: currentResLocation });
    }
     

    try {
      const tripId = tripDataFlag.addMealINBooking ? tripDataFlag.tripID : option.value;
      const formData = new FormData();
      formData.append('id', tripId);
      const response = await fetch(`${apiGlobal}/agent/trip_data_fetch.php`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      
      if (data.status && data.message === 'Success') {
        const tripData = data.data[0];
        const parsedStartDate = new Date(tripData.start_date);
        const parsedEndDate = new Date(tripData.end_date);
  
        setField('date', { startDate: parsedStartDate, endDate: parsedEndDate });

        setReInitialValue({
          paxDetails: data.pax_details || [],
          tripId: tripData.trip_uin,
          refno: tripData.trip_ref_no,
          triptype: tripData.trip_type,
          startDates: parsedStartDate,
          endDate: parsedEndDate,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Calculate total meal price
  const handleTotalMealsChange = (e) => {
    const newTotalMeal = parseInt(e.target.value, 10) || 0;
    setField('totalMeals', newTotalMeal);
    setField('mealTotalRate', newTotalMeal * parseInt(price, 10));
  };

  // Handle modal for adding trips
  const showTripModal = () => {
    setShowAddTripModal(true);
    setMealBookingData(prev => ({ ...prev, showMealBookingModal: false }));
  };

  // Form submission
  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedPaxs.length === 0) {
      toast.error('No Pax Selected');
      return;
    }
    
    if (selectedPaxs.length > state.totalMeals) {
      toast.error('Number of Paxs should be less than or equal to total meals');
      return;
    }

    if (!state.mealDate) {
      toast.error('Please select a meal date');
      return;
    }

    const { reInitialValue } = state;
    const { resLocation, timing, format: selectedFormat } = reInitialValue;
    const bookingDateIST = new Date(new Date(state.mealDate).getTime() + (5.5 * 60 * 60 * 1000));
    const formattedBookingDate = formatDate(bookingDateIST);
    const totalAmount = price * state.totalMeals;

    // Validate form fields
    if (!userId || !agentId || !state.selectedTrip?.value 
        || !resLocation?.value || !timing?.value 
        || !reInitialValue.dayname || !price || !state.totalMeals) {
      toast.error('All Fields are required');
      return;
    }

    if (typeof selectedFormat !== 'object' || selectedFormat === null) {
      toast.error('Select Format');
      return;
    }

    if (!id) {
      toast.error('meal id not found');
      return;
    }

    // Prepare form data
    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('agent_id', agentId);
    formData.append('booking_date', formattedBookingDate);
    formData.append('trip_id', state.selectedTrip.value);
    formData.append('location_id', resLocation.value);
    formData.append('meal_id', id);
    formData.append('meal_date', formattedBookingDate);
    formData.append('meal_offered', timing.value);
    formData.append('day_name', reInitialValue.dayname);
    formData.append('meal_amount', price);
    formData.append('total_meal', state.totalMeals);
    formData.append('total_amount', totalAmount);
    formData.append('pax_ids', selectedPaxs);

    formData.append('type', 1);

    try {
      const response = await fetch(`${apiGlobal}/agent/booking_add.php`, {
        method: 'POST',
        body: formData,
      }).then(res => res.json());
      
      if (response.status && response.message === 'Success') {
        toast.success('Meal Booking Successfully...');
        setMealBookingData(prev => ({
          ...prev,
          locations: '',
          mealOffereds: '',
          finalprice: '', 
          format: '', 
          id: '', 
          mealname: '', 
          restaurantname: '',
          showMealBookingModal: false,
        }));
      } else if (response.status && response.balance_flag === 1) {
        toast.error(response.message);
      } else {
        toast.error('Error in Meal Booking...');
      }
    } catch (error) {
      console.error(error);
      toast.error('Network error occurred');
    }
  };

  // Initial side effects
  useEffect(() => {
    if (!tripDataFlag.addMealINBooking) {
      fetchTripOption();
    }
  }, []);

  useEffect(() => {
    // Update total meals based on selected passengers
    setField('totalMeals', selectedPaxs.length);
    // Update total price
    setField('mealTotalRate', selectedPaxs.length * parseInt(price, 10));
  }, [selectedPaxs, price]);

  useEffect(() => {
    if (mealName && resName) {
      setReInitialValue({
        mealName, resName, price, id, mealOffereds, locations, format,
      });
    }
  }, [mealName, resName, price, id, mealOffereds, locations, format]);

  useEffect(() => {
    if (tripDataFlag.addMealINBooking) {
      const tripOption = {
        value: tripDataFlag.tripID,
        label: tripDataFlag.name,
      };
      
      setField('selectedTrip', tripOption);
      fetchTripData(null, tripOption);
    }
  }, [tripDataFlag]);

  // Memoized options
  const restaurantLocationsOptions = useMemo(
() => state.reInitialValue?.locations?.map(location => ({
      value: location.id,
      label: location.restaurant_name,
    })) || [], 
  [state.reInitialValue?.locations],
);

  const filteredTimingOption = useMemo(() => {
    const mealOfferedArray = (state.reInitialValue?.mealOffereds || '').split(',').map(item => item.trim());
    return TIMING_OPTIONS.filter(item => mealOfferedArray.includes(item.value));
  }, [state.reInitialValue?.mealOffereds]);

  const filteredTypeOption = useMemo(() => {
    const formatString = typeof state.reInitialValue?.format === 'string' ? state.reInitialValue.format : '';
    const mealFormatArray = formatString.split(',').map(item => item.trim());
    return TYPE_OPTIONS.filter(item => mealFormatArray.includes(String(item.value)));
  }, [state.reInitialValue?.format]);

  // Extracted shared props for better readability
  const readOnlyProps = {
    InputProps: { readOnly: true },
  };
  
  const datePickerProps = {
    type: 'date',
    InputLabelProps: { shrink: true },
  };
  
  // Format helper for displaying trip dates
  const getTripDate = dateObj => (dateObj ? formatDate(dateObj) : '');
  
  return (
    <div style={{
      display: 'flex',
    }}
    >
      <form onSubmit={handleBookingSubmit}>
        {/* Trip selection */}
        <Grid container spacing={2}>
          <Grid item xs={6} className="m-2">
            <Autocomplete
              options={state.tripOption}
              getOptionLabel={option => option.label || ''}
              renderInput={params => <TextField {...params} label="Trip Option" variant="outlined" />}
              size="small"
              onChange={(event, option) => fetchTripData(event, option)}
              value={state.selectedTrip}
            />
          </Grid>
        
          {!tripDataFlag.addMealINBooking && (
          <Grid item xs={2} className="m-2">
            <Button variant="primary-outline" type="button" fullWidth onClick={showTripModal}>
              Add trip
            </Button>
          </Grid>
        )}
        
          {/* Trip details section */}
          <Grid container spacing={2} className="m-2">
            <Grid item xs={3}>
              <TextField
                label="Trip ID"
                variant="outlined"
                fullWidth
                name="tripID"
                size="small"
                value={state?.reInitialValue?.tripId || ''}
                InputLabelProps={{ shrink: !!state.reInitialValue.tripId }}
                {...readOnlyProps}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                label="Ref No"
                variant="outlined"
                fullWidth
                name="refNo"
                size="small"
                value={state?.reInitialValue?.refno || ''}
                InputLabelProps={{ shrink: !!state.reInitialValue.refno }}
                {...readOnlyProps}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                label="Start Date"
                variant="outlined"
                fullWidth 
                value={getTripDate(state?.reInitialValue?.startDates)}
                name="sDate"
                size="small"
                {...datePickerProps}
                {...readOnlyProps}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                label="End Date"
                variant="outlined"
                fullWidth 
                value={getTripDate(state?.reInitialValue?.endDate)}
                name="eDate"
                size="small"
                {...datePickerProps}
                {...readOnlyProps}
              />
            </Grid>
          </Grid>
        
          {/* Meal details section */}
          <Grid container spacing={2} className="m-2">
            <Grid item xs={4}>
              <TextField
                label="Meal Name"
                variant="outlined"
                fullWidth
                name="mealName"
                size="small"
                value={state?.reInitialValue?.mealName || ''}
                {...readOnlyProps}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                label="Restaurant Name"
                variant="outlined"
                fullWidth
                name="resName"
                size="small"
                value={state?.reInitialValue?.resName || ''}
                {...readOnlyProps}
              />
            </Grid>

            <Grid item xs={4}>
              <Autocomplete
                disablePortal
                size="small"
                options={restaurantLocationsOptions}
                renderInput={params => <TextField {...params} label="Select Res Location" />}
                onChange={(e, option) => handleFieldChange('resLocation', option)}
                ref={autocompleteRefs.location}
                value={state.reInitialValue.resLocation || null}
              />
            </Grid>
          </Grid>

          {/* Meal scheduling section */}
          <Grid container spacing={2} className="m-2">
            <Grid item xs={4}>
              <TextField
                label="Meal Date"
                variant="outlined"
                fullWidth
                name="mealDate"
                size="small"
                {...datePickerProps}
                value={state.mealDate || ''}
                inputProps={{
                min: getTripDate(state?.reInitialValue?.startDates),
                max: getTripDate(state?.reInitialValue?.endDate),
              }}
                onChange={e => handleMealDateChange(e.target.value)}
                disabled={!state.selectedTrip}
                sx={{ color: 'red' }}
              />

              {state.selectedTrip && (
              <p style={{ color: 'red' }}>
                Meal date available between{' '}
                <span>
                  {formatDateToDDMMYYYY(getTripDate(state?.reInitialValue?.startDates))}
                </span> 
                /
                <span>
                  {formatDateToDDMMYYYY(getTripDate(state?.reInitialValue?.endDate))}
                </span> 
              </p>
            )}
            </Grid>
        
            <Grid item xs={4}>
              <TextField
                label="Day Name"
                variant="outlined"
                fullWidth
                name="dayName"
                size="small"
                value={state?.reInitialValue?.dayname || ''}
                InputLabelProps={{ shrink: !!state?.reInitialValue?.dayname }}
                {...readOnlyProps}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                label="Total Meal"
                variant="outlined"
                fullWidth
                name="totalmeal"
                size="small"
                value={selectedPaxs.length || 0}
                InputProps={{ readOnly: true }}
                onChange={handleTotalMealsChange}
                type="number"
                disabled={!state.selectedTrip}
              />
            </Grid>
          </Grid>
        
          {/* Meal configuration section */}
          <Grid container spacing={2} className="m-2">
            <Grid item xs={4}>
              <Autocomplete
                disablePortal
                size="small"
                options={filteredTimingOption}
                renderInput={params => <TextField {...params} label="Select Timing" placeholder="Select meal timing" />}
                onChange={(e, option) => handleFieldChange('timing', option)}
                ref={autocompleteRefs.timing}
                disabled={!state.mealDate}
                value={state.reInitialValue.timing || null}
                isOptionEqualToValue={(option, value) => option.value === value.value}
              />
            </Grid>

            <Grid item xs={4}>
              <Autocomplete
                disablePortal
                disableClearable
                size="small"
                options={filteredTypeOption}
                renderInput={params => <TextField {...params} label="Select Format" placeholder="Select format type" />}
                onChange={(e, option) => handleFieldChange('format', option)}
                ref={autocompleteRefs.format}
                disabled={!state.mealDate}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                label="Total Amount"
                variant="outlined"
                fullWidth
                name="totalamount"
                size="small"
                value={state?.mealTotalRate || ''}
                InputLabelProps={{ shrink: true }}
                {...readOnlyProps}
              />
            </Grid>
          </Grid>

          {/* Trip type radio buttons */}
          <Grid item xs={4} className="m-2">
            <FormControlLabel
              value="1"
              control={<Radio checked={state?.reInitialValue?.triptype === '1'} readOnly />}
              label="Group"
              name="triptype"
            />
            <FormControlLabel
              value="2"
              control={<Radio checked={state?.reInitialValue?.triptype === '2'} readOnly />}
              label="Family"
              name="triptype"
            />
          </Grid>

          {/* Pax details table */}
          <Grid container spacing={2} className="m-2">
            <Grid item xs={12}>
              <PaxDetailsTable 
                paxDetails={state.paxData || []}
                selectedPaxs={selectedPaxs}
                setSelectedPaxs={setSelectedPaxs}
                triptype={state.reInitialValue.triptype}
              />
            </Grid>
          </Grid>

          {/* Submit button */}
          <Grid item xs={12}>
            <Button
              type="button"
              variant="danger"
              onClick={() => setMealBookingData(prevState => ({
          ...prevState,
          showMealBookingModal: false,
        }))}>Close
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              fullWidth
              disabled={!state.selectedTrip || !state.mealDate || !state.selecttimingOption}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </form>
      <div style={{
  border: '1px solid #ccc',
  padding: '20px',
  borderRadius: '10px',
  marginTop: '20px',
  width: '80%',
  maxHeight: '100vh', // Use viewport height for scrollable area
  overflowY: 'auto', // Enable vertical scroll
}}
      >
        <h5>Trip Booking Details</h5>
        <hr />
        {tripBookingData?.length > 0 && tripBookingData.map((tripData, index) => (
          <ShowTripInfo key={tripData.id || index} tripBookingInfo={tripData} />
  ))}
      </div>



    </div>
    

  );
}; 

BookingForm.propTypes = {
  setMealBookingData: PropTypes.func.isRequired,
  mealName: PropTypes.string.isRequired,
  resName: PropTypes.string.isRequired,
  price: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  mealOffereds: PropTypes.string.isRequired,
  format: PropTypes.string.isRequired,
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      restaurant_name: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

export default BookingForm;
